import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, X } from "lucide-react";

/**
 * CookieBanner — GDPR-compliant consent banner.
 * Persists user choice in localStorage as JSON: { analytics, marketing, timestamp }.
 * Re-asks every 12 months.
 */
const STORAGE_KEY = "nexum-cookie-consent";
const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000; // 12 months

const readConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.timestamp) return null;
    if (Date.now() - parsed.timestamp > CONSENT_MAX_AGE_MS) return null;
    return parsed;
  } catch (err) {
    console.warn('readConsent failed:', err);
    return null;
  }
};

const writeConsent = (choice) => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...choice, timestamp: Date.now() })
    );
    window.dispatchEvent(new CustomEvent("nexum-cookie-consent", { detail: choice }));
  } catch (err) {
    console.warn('writeConsent failed:', err);
  }
};

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [lang, setLang] = useState("es");

  useEffect(() => {
    setLang(localStorage.getItem("nexum-lang") || "es");
    // Small delay so it doesn't compete with hero paint
    const t = setTimeout(() => {
      if (!readConsent()) setVisible(true);
    }, 900);
    return () => clearTimeout(t);
  }, []);

  const accept = () => {
    writeConsent({ analytics: true, marketing: false });
    setVisible(false);
  };
  const reject = () => {
    writeConsent({ analytics: false, marketing: false });
    setVisible(false);
  };
  const savePrefs = () => {
    writeConsent({ analytics, marketing: false });
    setVisible(false);
  };

  const T = {
    es: {
      heading: "Cookies",
      body: "Usamos cookies técnicas necesarias para que la web funcione y, opcionalmente, cookies analíticas para entender cómo se usa. Ninguna publicidad.",
      linkTxt: "Ver Política de Cookies",
      accept: "Aceptar todas",
      reject: "Solo esenciales",
      customize: "Personalizar",
      close: "Cerrar",
      details: {
        essentialTitle: "Esenciales",
        essentialDesc: "Necesarias para el funcionamiento del sitio. Siempre activas.",
        analyticsTitle: "Analíticas",
        analyticsDesc: "Nos ayudan a entender qué secciones funcionan mejor. Anónimas.",
        save: "Guardar preferencias",
      },
    },
    en: {
      heading: "Cookies",
      body: "We use technical cookies required for the site to work and, optionally, analytics cookies to understand how it's used. No ads.",
      linkTxt: "See Cookie Policy",
      accept: "Accept all",
      reject: "Only essentials",
      customize: "Customize",
      close: "Close",
      details: {
        essentialTitle: "Essential",
        essentialDesc: "Required for the site to work. Always on.",
        analyticsTitle: "Analytics",
        analyticsDesc: "Help us understand which sections work best. Anonymous.",
        save: "Save preferences",
      },
    },
  }[lang];

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-0 inset-x-0 z-[9990] px-3 pb-3 sm:px-5 sm:pb-5"
          role="dialog"
          aria-modal="false"
          aria-labelledby="cookie-heading"
          data-testid="cookie-banner"
        >
          <div className="max-w-[720px] mx-auto border border-white/[0.08] bg-[#0a0a0a]/95 backdrop-blur-xl shadow-[0_20px_80px_-20px_rgba(0,0,0,0.9)] rounded-lg overflow-hidden">
            <div className="p-4 sm:p-5">
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-[10px] tracking-[0.22em] uppercase text-zinc-500" id="cookie-heading">
                      {T.heading}
                    </span>
                  </div>
                  <p className="text-[13px] leading-relaxed text-zinc-300">
                    {T.body}{" "}
                    <Link
                      to="/cookies"
                      className="text-white underline underline-offset-2 hover:text-zinc-200 whitespace-nowrap"
                    >
                      {T.linkTxt} →
                    </Link>
                  </p>
                </div>
                <button
                  onClick={reject}
                  aria-label={T.close}
                  className="text-zinc-500 hover:text-white transition-colors flex-shrink-0"
                  data-testid="cookie-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Preferences */}
              <AnimatePresence>
                {showDetails && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-4 pt-4 border-t border-white/[0.06] space-y-3">
                      {/* Essential */}
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-4 rounded-full bg-emerald-500/40 border border-emerald-500/50 flex items-center justify-end px-0.5 flex-shrink-0 mt-0.5">
                          <div className="w-3 h-3 rounded-full bg-emerald-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[12px] font-medium text-white">{T.details.essentialTitle}</p>
                          <p className="text-[11px] text-zinc-500 leading-snug">{T.details.essentialDesc}</p>
                        </div>
                      </div>
                      {/* Analytics */}
                      <div className="flex items-start gap-3">
                        <button
                          type="button"
                          onClick={() => setAnalytics((v) => !v)}
                          className={`w-8 h-4 rounded-full border flex items-center px-0.5 flex-shrink-0 mt-0.5 transition-all ${
                            analytics
                              ? "bg-white/90 border-white justify-end"
                              : "bg-white/5 border-white/15 justify-start"
                          }`}
                          data-testid="cookie-toggle-analytics"
                          aria-pressed={analytics}
                        >
                          <div className={`w-3 h-3 rounded-full transition-colors ${analytics ? "bg-black" : "bg-white/40"}`} />
                        </button>
                        <div className="flex-1 min-w-0">
                          <p className="text-[12px] font-medium text-white">{T.details.analyticsTitle}</p>
                          <p className="text-[11px] text-zinc-500 leading-snug">{T.details.analyticsDesc}</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Actions */}
              <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
                <button
                  onClick={() => setShowDetails((v) => !v)}
                  className="text-[12px] text-zinc-500 hover:text-white transition-colors px-2 py-1.5"
                  data-testid="cookie-customize"
                >
                  {showDetails ? T.details.save : T.customize}
                </button>
                {showDetails ? (
                  <button
                    onClick={savePrefs}
                    className="px-4 py-2 text-[12px] font-medium text-white bg-white/10 hover:bg-white/15 border border-white/15 transition-colors"
                    data-testid="cookie-save"
                  >
                    {T.details.save}
                  </button>
                ) : (
                  <>
                    <button
                      onClick={reject}
                      className="px-4 py-2 text-[12px] font-medium text-white bg-transparent hover:bg-white/[0.06] border border-white/15 transition-colors"
                      data-testid="cookie-reject"
                    >
                      {T.reject}
                    </button>
                    <button
                      onClick={accept}
                      className="px-4 py-2 text-[12px] font-medium text-black bg-white hover:bg-zinc-200 transition-colors flex items-center gap-1.5"
                      data-testid="cookie-accept"
                    >
                      <Check className="w-3 h-3" strokeWidth={2.5} />
                      {T.accept}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
